import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import { useCallback, useMemo } from 'react'
import { Nullish, UserInfoInOrganization } from 'types'
import { ManagerRole } from '../UserOrgRolesAccessCodes/useOrgAccessCodes'

export function UserOrgRolesManagerSelect({
  value,
  role,
  options,
  onChange,
}: {
  value: Nullish<UserInfoInOrganization>
  role: ManagerRole
  options: UserInfoInOrganization[]
  onChange: (user: Nullish<UserInfoInOrganization>) => void
}) {
  const handleChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const userId = event.target.value
      const selected = options.find((user) => user.id === userId) ?? null
      onChange(selected)
    },
    [onChange, options],
  )

  const renderSelectedOption = useCallback(
    (selected: number) => {
      if (!selected) return 'Assign User'

      const user = options.find((item) => item.id === selected)
      if (!user) return 'Assign User'

      return (
        <Box display="flex" flexDirection="column">
          <Typography
            variant="body2"
            lineHeight={1.3}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {user.firstname} {user.lastname}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            overflow="hidden"
            textOverflow="ellipsis"
            lineHeight={1.1}
          >
            {user.email}
          </Typography>
        </Box>
      )
    },
    [options],
  )

  const hasError = useMemo(() => {
    return (role === 'owner' || role === 'supervisor') && !value
  }, [role, value])

  return (
    <FormControl error={hasError} fullWidth>
      <Select
        size="small"
        value={value?.id ?? ''}
        displayEmpty
        onChange={handleChange}
        renderValue={renderSelectedOption}
      >
        <MenuItem value="">Assign User</MenuItem>

        {options.map((user) => (
          <MenuItem key={user.id} value={user.id}>
            {user.firstname} {user.lastname} ({user.email})
          </MenuItem>
        ))}
      </Select>

      {hasError && <FormHelperText>Please select a user</FormHelperText>}
    </FormControl>
  )
}
